import { defineStore } from "pinia";

export const useFullSongPlaybackStore = defineStore(
  "full-song-playback",
  () => {
    const spotify = useSpotifyStore();
    const apple = useAppleMusicStore();
    const beta = useBetaStore();
    const modals = useModalStore();
    const debug = useDebug();
    const mediaplayer = useMediaPlayerStore();
    const api = useApiStore();

    const integration = ref<"apple" | "spotify" | null>(null);
    const nextIntegration = ref<"apple" | "spotify" | null>(null);
    const integrationTerm = computed(() => {
      if (!beta.features.fullSongPlayback) {
        return "";
      }
      if (integration.value === "apple") {
        return "Apple Music";
      } else if (integration.value === "spotify") {
        return "Spotify";
      }
      return "";
    });

    function setIntegration(newIntegration: "apple" | "spotify") {
      if (!beta.features.fullSongPlayback) {
        return;
      }

      if (integration.value === newIntegration) {
        nextIntegration.value = null;
      } else {
        nextIntegration.value = newIntegration;
      }

      if (integration.value) {
        modals.showModal("ConfirmDisconnectIntegrationModal");
      } else {
        authorize(newIntegration);
      }
    }

    function setIntegrationConfirmed() {
      if (!beta.features.fullSongPlayback) {
        return;
      }
      if (integration.value) {
        unauthorize();
        integration.value = null;
      }

      if (nextIntegration.value) {
        authorize(nextIntegration.value);
      }
    }

    function unauthorize() {
      if (!beta.features.fullSongPlayback) {
        return;
      }
      mediaplayer.pause();
      if (integration.value === "apple") {
        apple.unauthorize();
      } else if (integration.value === "spotify") {
        spotify.unauthorize();
      }
    }

    function authorize(service: "apple" | "spotify") {
      if (!beta.features.fullSongPlayback) {
        return;
      }
      if (service === "apple") {
        apple.authorize();
      } else if (service === "spotify") {
        spotify.authorize();
      }
    }

    async function saveSettings(
      authInfo: any,
      service: "apple" | "spotify" | null,
    ) {
      if (service === integration.value) {
        const data = {
          musicPlayerPreferences: JSON.stringify({
            authInfo: (authInfo && JSON.stringify(authInfo)) || null,
            service,
          }),
        };
        await api.post("/UpdateMusicPlayerPreferences ", data);
      } else if (service !== integration.value) {
        // purposely ignore this case
        debug.log("ignoring save settings", authInfo, service);
      }
    }

    watch(integration, () => {
      if (!integration.value) {
        saveSettings(null, null);
      }
    });

    if (beta.features.fullSongPlayback) {
      watch(
        () => [apple.authorized, spotify.authorized],
        () => {
          mediaplayer.reset();
          if (apple.authorized) {
            integration.value = "apple";
          } else if (spotify.authorized) {
            integration.value = "spotify";
          } else {
            integration.value = null;
          }
          (window as any).dataLayer.push({
            event: 'dspIntegration',
            dsp_integration_status: integration.value ? true : false,
            dsp_integration_platform: integration.value,
          })
          // if (mediaplayer.nowPlaying) {
          //   mediaplayer.playMediaItem(mediaplayer.nowPlaying.id, true);
          // }
        },
      );
    }

    const connecting = computed(
      () =>
        (beta.features.fullSongPlayback &&
          (apple.connecting || spotify.connecting)) ||
        false,
    );

    return {
      integration,
      nextIntegration,
      connecting,
      integrationTerm,
      setIntegration,
      setIntegrationConfirmed,

      saveSettings,
    };
  },
);
